// Old v1/v2 addresses keep working (API-v3 13): each one forwards to its
// new place. Some need the current project (the one last opened, else the
// first one), some ask the server where a record lives (GET /api/lookup).
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router";
import type { LookupResult, MeV3 } from "@convenor/shared/v3";
import { api } from "../api/client";
import { qk } from "../api/keys";
import { lastProject, useProjectList } from "../api/project";
import { Loading, NotFoundContent } from "../ui/QueryStates";

/** The project to use when a URL has none. null = no project yet. */
export function useCurrentProjectId(): { id: string | null; loading: boolean } {
  const list = useProjectList();
  if (list.isPending) return { id: null, loading: true };
  const items = list.data ?? [];
  const last = lastProject();
  const id = (last && items.some((p) => p.id === last) ? last : null) ?? items.find((p) => p.status !== "closed")?.id ?? items[0]?.id ?? null;
  return { id, loading: false };
}

const fill = (template: string, params: Record<string, string | undefined>) => template.replace(/:(\w+)/g, (m, k: string) => params[k] ?? m);

/**
 * Where "/" goes when there is no project: the server's Me.home when that is
 * /org (an org with an org tree but no projects lands on its people and
 * organisation screens) or /welcome/done (a new owner chooses how to start the
 * first project), else the projects page.
 */
export function noProjectHome(home: string | undefined): string {
  return home === "/org" || home === "/welcome/done" ? home : "/projects";
}

/** "/" and old section links: /p/<current><suffix>, or the no-project home (noProjectHome). */
export function ToCurrentProject({ suffix = "" }: { suffix?: string }) {
  const params = useParams();
  const { search } = useLocation();
  const { id, loading } = useCurrentProjectId();
  const me = useQuery({ queryKey: qk.me, queryFn: () => api.get<MeV3>("/api/me"), retry: false, staleTime: 60_000 });
  if (loading) return <Loading />;
  if (!id) {
    if (suffix) return <Navigate to="/projects" replace />;
    if (me.isPending) return <Loading />;
    return <Navigate to={noProjectHome(me.data?.home)} replace />;
  }
  const target = `/p/${id}${fill(suffix, params)}`;
  const joined = target.includes("?") ? target + (search ? `&${search.slice(1)}` : "") : target + search;
  return <Navigate to={joined} replace />;
}

/** A static rename: /login -> /sign-in (query kept). */
export function Rename({ to }: { to: string }) {
  const params = useParams();
  const { search } = useLocation();
  return <Navigate to={`${fill(to, params)}${search}`} replace />;
}

/** Asks the server which project a record belongs to, then forwards. */
export function LookupRedirect({ entity, to }: { entity: "tor" | "event" | "report" | "engagement" | "contact"; to: (r: LookupResult, params: Record<string, string | undefined>) => string }) {
  const params = useParams();
  const id = params.id ?? "";
  const q = useQuery({
    queryKey: ["lookup", entity, id],
    queryFn: () => api.get<LookupResult>(`/api/lookup/${entity}/${id}`),
    retry: false,
  });
  if (q.isPending) return <Loading />;
  if (q.isError || !q.data.projectId) return <NotFoundContent />;
  return <Navigate to={to(q.data, params)} replace />;
}

/**
 * /invite/:token is the guest RSVP page in v3, but team invitations sent
 * before v3 used the same address: those go to /join/:token. GET /api/join/:token
 * answers for a team or partner invitation that is still open (the old
 * /api/invitations/:token is gone, so every link stayed on the RSVP page).
 */
export function TeamInviteGate({ children }: { children: ReactNode }) {
  const { token = "" } = useParams();
  const q = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => api.get<unknown>(`/api/join/${encodeURIComponent(token)}`),
    retry: false,
  });
  if (q.isPending) return <Loading />;
  if (q.isSuccess) return <Navigate to={`/join/${encodeURIComponent(token)}`} replace />;
  return <>{children}</>;
}
